import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Lock, MapPin, User as UserIcon, ScrollText, ChevronDown, ChevronUp } from "lucide-react";
import { colors } from "../theme";
import { useAuth } from "../store/useAuth";
import { TOS_VERSION } from "../data/terms";
import { TermsContent } from "./TermsContent";

export default function AuthModal() {
  const { modalOpen, modalMode, error, register, login, closeModal, openModal } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [tos, setTos] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!modalOpen) return null;
  const isSignup = modalMode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (isSignup) {
      await register({ email, password, name, location, tos_version: TOS_VERSION });
    } else {
      await login(email, password);
    }
    setBusy(false);
  };

  return createPortal(
    <div style={s.overlay} data-testid="auth-modal">
      <div style={s.card}>
        <button style={s.close} onClick={closeModal} data-testid="auth-close" aria-label="Close"><X size={18} /></button>
        <div style={s.brand}>Resource Circularity Engine</div>
        <h2 style={s.title}>{isSignup ? "Create your account" : "Welcome back"}</h2>
        <p style={s.sub}>{isSignup ? "Save scenarios & sync across devices." : "Sign in to access your saved work."}</p>

        <form onSubmit={submit}>
          {isSignup && (
            <div style={s.field}><UserIcon size={15} style={s.ficon} />
              <input style={s.input} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name" />
            </div>
          )}
          <div style={s.field}><Mail size={15} style={s.ficon} />
            <input style={s.input} type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email" />
          </div>
          <div style={s.field}><Lock size={15} style={s.ficon} />
            <input style={s.input} type="password" required placeholder={isSignup ? "Password (min 8 chars)" : "Password"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password" />
          </div>
          {isSignup && (
            <div style={s.field}><MapPin size={15} style={s.ficon} />
              <input style={s.input} placeholder="Location (city / country)" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="auth-location" />
            </div>
          )}
          {isSignup && (
            <>
              <label style={s.tosRow} data-testid="auth-tos-row">
                <input type="checkbox" checked={tos} onChange={(e) => setTos(e.target.checked)} data-testid="auth-tos" />
                <span>I agree to the Beta Terms of Service & data use (v{TOS_VERSION}).</span>
              </label>
              <button type="button" style={s.termsToggle} onClick={() => setShowTerms((v) => !v)} data-testid="auth-terms-toggle">
                <ScrollText size={13} />
                {showTerms ? "Hide the full Terms of Service" : "Read the full Terms of Service"}
                {showTerms ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              {showTerms && (
                <div style={s.termsBox} data-testid="auth-terms-full"><TermsContent /></div>
              )}
            </>
          )}
          {error && <div style={s.error} data-testid="auth-error">{error}</div>}
          <button type="submit" style={{ ...s.submit, ...((busy || (isSignup && !tos)) ? s.submitOff : {}) }}
            disabled={busy || (isSignup && !tos)} data-testid="auth-submit">
            {busy ? "Please wait…" : isSignup ? "Create account" : "Sign in"}
          </button>
        </form>

        <div style={s.switch}>
          {isSignup ? "Already have an account? " : "New here? "}
          <button style={s.switchBtn} onClick={() => openModal(isSignup ? "login" : "signup")} data-testid="auth-switch">
            {isSignup ? "Sign in" : "Create one"}
          </button>
        </div>
      </div>
    </div>, document.body);
}

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,32,0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4000 },
  card: { position: "relative", width: 380, maxWidth: "92vw", maxHeight: "92vh", overflowY: "auto", background: "#fff", borderRadius: 20, padding: "30px 28px", boxShadow: "0 24px 70px rgba(0,0,0,0.28)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" },
  close: { position: "absolute", top: 14, right: 14, border: "none", background: "transparent", cursor: "pointer", color: colors.textTertiary },
  brand: { fontSize: 10.5, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: colors.brand },
  title: { fontSize: 22, fontWeight: 800, color: colors.textPrimary, margin: "8px 0 3px" },
  sub: { fontSize: 12.5, color: colors.textTertiary, margin: "0 0 18px" },
  oauthBtn: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, padding: "11px", marginBottom: 9, borderRadius: 12, border: `1.5px solid ${colors.border}`, background: "#fff", fontSize: 13.5, fontWeight: 600, color: colors.textPrimary, cursor: "pointer" },
  termsToggle: { display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", margin: "0 2px 10px", padding: "6px 11px", borderRadius: 999, border: `1px solid ${colors.brand}44`, background: `${colors.brand}0F`, color: colors.brand, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  termsBox: { maxHeight: 200, overflowY: "auto", margin: "0 0 12px", padding: "10px 12px", borderRadius: 12, background: "rgba(0,0,0,0.02)", border: `1px solid ${colors.border}` },
  divider: { display: "flex", alignItems: "center", gap: 10, margin: "6px 0 14px" },
  divLine: { flex: 1, height: 1, background: colors.border },
  divText: { fontSize: 11, color: colors.textTertiary },
  field: { display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${colors.border}`, borderRadius: 11, padding: "0 11px", marginBottom: 9 },
  ficon: { color: colors.textTertiary, flexShrink: 0 },
  input: { flex: 1, border: "none", outline: "none", padding: "11px 0", fontSize: 13.5, color: colors.textPrimary, background: "transparent" },
  tosRow: { display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11.5, color: colors.textSecondary, margin: "4px 2px 12px", cursor: "pointer" },
  error: { background: "#fdecec", color: "#c0392b", fontSize: 12, padding: "8px 11px", borderRadius: 9, marginBottom: 10 },
  submit: { width: "100%", padding: "12px", borderRadius: 12, border: "none", background: colors.brand, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 18px rgba(81,170,254,0.4)" },
  submitOff: { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" },
  switch: { textAlign: "center", fontSize: 12.5, color: colors.textTertiary, marginTop: 16 },
  switchBtn: { border: "none", background: "transparent", color: colors.brand, fontWeight: 700, cursor: "pointer", fontSize: 12.5 },
};
